/**
 * React hook for notification template CRUD, following the useRulesEngine pattern.
 */
import { useCallback, useEffect, useState } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import type { ActivityDefinition } from '@medplum/fhirtypes';

import type { NotificationTemplateDraft } from '../notification-template.types';
import {
  listNotificationTemplates,
  createNotificationTemplate,
  updateNotificationTemplate,
  archiveNotificationTemplate,
  activityDefinitionToDraft,
  findActiveRulesReferencingEmailTemplate,
  type ActiveRuleReference,
} from '../notification-template.medplum';

export interface ReloadOptions {
  /** When true, refresh the list without toggling `loading` (avoids unmounting the template editor). */
  silent?: boolean;
}

export interface UseNotificationTemplatesResult {
  templates: ActivityDefinition[];
  loading: boolean;
  error: Error | null;
  reload: (options?: ReloadOptions) => Promise<void>;
  save: (draft: NotificationTemplateDraft) => Promise<ActivityDefinition>;
  archive: (id: string) => Promise<void>;
  checkArchiveSafety: (id: string) => Promise<ActiveRuleReference[]>;
  getDraft: (id: string) => NotificationTemplateDraft | null;
}

export function useNotificationTemplates(): UseNotificationTemplatesResult {
  const medplum = useMedplum();

  const [templates, setTemplates] = useState<ActivityDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const reload = useCallback(async (options?: ReloadOptions): Promise<void> => {
    const silent = options?.silent === true;
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const results = await listNotificationTemplates(medplum);
      setTemplates(results);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load notification templates'));
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [medplum]);

  useEffect(() => {
    reload();
  }, [reload]);

  const save = useCallback(
    async (draft: NotificationTemplateDraft): Promise<ActivityDefinition> => {
      const saved = draft.id
        ? await updateNotificationTemplate(medplum, draft)
        : await createNotificationTemplate(medplum, draft);
      await reload({ silent: true });
      return saved;
    },
    [medplum, reload],
  );

  const archive = useCallback(
    async (id: string): Promise<void> => {
      await archiveNotificationTemplate(medplum, id);
      await reload({ silent: true });
    },
    [medplum, reload],
  );

  const checkArchiveSafety = useCallback(
    async (id: string): Promise<ActiveRuleReference[]> => {
      return findActiveRulesReferencingEmailTemplate(medplum, id);
    },
    [medplum],
  );

  const getDraft = useCallback(
    (id: string): NotificationTemplateDraft | null => {
      const ad = templates.find((t) => t.id === id);
      if (!ad) return null;
      return activityDefinitionToDraft(ad);
    },
    [templates],
  );

  return { templates, loading, error, reload, save, archive, checkArchiveSafety, getDraft };
}
